import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { Appointment, AppointmentState, dtoDate, ResourceUnavailableApt } from '../shared/model/appointment-book.models';
import { BookAptInfo } from '../shared/model/BookAptInfo';
import { Resource } from '../shared/model/resource.model';
import { StartAppointmentRequest } from '../shared/model/TMaxUpdateInfo.model';
import { APIResult, WebAPIResult } from './employee.service';
import { EventService } from './event.service';
import * as dayjs from 'dayjs';
import { ConstantsService } from './constants.service';
import { SessionDelay } from '../shared/components/edit-session-delays/edit-session-delays.component';

@Injectable({
  providedIn: 'root'
})
export class AppointmentBookService {
  
  constructor(private http: HttpClient, private events: EventService) { }

  public CreateOrUpdateResourceUnavailableApointment(apt: ResourceUnavailableApt): Observable<APIResult> {
    apt.dtoStart = dtoDate.CreateFrom(apt.StartTime);
    apt.dtoEnd = dtoDate.CreateFrom(apt.EndTime);

    return this.http.post<WebAPIResult>('/api/FrontOffice/AppointmentBook/UpdateUnavailableResourceApt', apt).pipe(
      take(1),
      catchError((e) => {
        var result: APIResult = new APIResult();
        result.Success = 0;
        result.Msg = e;
        return of(result);
      }),
      map((m) => {
        // console.log("Resource Availability Updated");
        this.events.ResourceWorkTimeUpdated();
        var result: APIResult = new APIResult();
        result.Success = m.Success;
        result.Msg = m.Msg;
        return result;
      })
    );
  }

  public CreateOrUpdateApointmentView(Id: number, Name: string, Branch: number, Resources: number[]): Observable<APIResult> {
    var data = {
      Id: Id,
      Name: Name,
      Branch: Branch,
      Resources: Resources,
    };
    return this.http.post<WebAPIResult>('/api/Backoffice/AppointmentViews/CreateOrUpdateAppointmentView', data).pipe(
      take(1),
      catchError((e) => {
        // console.log("Resource Availability Wizard Error");
        // console.log(e);
        var result: APIResult = new APIResult();
        result.Success = 0;
        result.Msg = e;
        return of(result);
      }),
      map((m) => {
        // console.log("Resource Availability Updated");
        var result: APIResult = new APIResult();
        result.Success = 1;
        result.Msg = m.Msg;
        return result;
      })
    );
  }

  DeleteAppointment(Id: number): Observable<APIResult> {
    var data = {
      AppointmentId: Id,
    };
    return this.http.post<WebAPIResult>('/api/FrontOffice/AppointmentBook/DeleteAppointment', data).pipe(
      take(1),
      catchError((e) => {
        var result: APIResult = new APIResult();
        result.Success = 0;
        result.Msg = e;
        return of(result);
      }),
      map((m) => {
        var result: APIResult = new APIResult();
        result.Success = 1;
        result.Msg = m.Msg;
        return result;
      })
    );
  }

  DeleteResourceUnavailable(Id: number): Observable<APIResult> {
    var data = {
      Id: Id,
    };
    return this.http.post<WebAPIResult>('/api/FrontOffice/AppointmentBook/DeleteUnavailableResourceApt', data).pipe(
      take(1),
      catchError((e) => {
        var result: APIResult = new APIResult();
        result.Success = 0;
        result.Msg = e;
        return of(result);
      }),
      map((m) => {
        var result: APIResult = new APIResult();
        result.Success = 1;
        result.Msg = m.Msg;
        return result;
      })
    );
  }

  CreateOrUpdateAppointment(aptInfo: BookAptInfo): Observable<BookAptInfo> {
    
    var data = {
      AppointmentId: aptInfo.appointmentId,
      Branch: aptInfo.branchId,
      ClientId: aptInfo.clientId,
      SessionId: aptInfo.sessionId,
      ResourceId: aptInfo.resourceId,
      StartDateTime: aptInfo.startTime,
      Employee: aptInfo.employee,
      PreSessionDelay: aptInfo.PreSessionDelay,
      PostSessionDelay: aptInfo.PostSessionDelay,
      State: aptInfo.State,
      Terminal: aptInfo.Terminal,
      Start : dtoDate.CreateFrom(aptInfo.startTime),
      Locked: aptInfo.Locked,
    };

    // Make sure we use correct start time
    if (aptInfo.apt && aptInfo.appointmentId > 0) {
      data.StartDateTime = aptInfo.apt.StartDateTime;     
    }
    
    if (aptInfo.resourceId == null)
      throw new RangeError("resourceId value not set to valid resource and it is required");
    if (aptInfo.sessionId == null)
      throw new RangeError("sessionId value not set to valid session and it is required");
    if (aptInfo.clientId == null)
      throw new RangeError("clientId value not set to valid client and it is required");
    if (aptInfo.startTime == null)
      throw new RangeError("startTime value not set and it is required");
    if (aptInfo.PreSessionDelay == null)
      throw new RangeError("PreSessionDelay value not set and it is required");
    if (aptInfo.PostSessionDelay == null)
      throw new RangeError("PostSessionDelay value not set and it is required");
    if (aptInfo.Terminal == null)
      throw new RangeError("Terminal value not set and it is required");
    if (aptInfo.State == null)
      throw new RangeError("Appointment State value not set and it is required");
    if (aptInfo.employee == null)
      throw new RangeError("Employee value not set and it is required");
    if (aptInfo.branchId == null)
      throw new RangeError("BranchId value not set and it is required");

    return this.http.post<BookAptInfo>('/api/FrontOffice/AppointmentBook/CreateOrUpdateAppointment', data).pipe(
      take(1),
      catchError((e) => {
        var result: APIResult = new APIResult();
        result.Success = 0;
        result.Msg = e;
        return EMPTY;
      }),
      map((m) => {
        return m;
      })
    );
  }

  AttendAppointment(aptId: number): Observable<APIResult> {
    var data = {
      AppointmentId: aptId,
      AttendedDateTime: dayjs()
    };
    return this.http.post<APIResult>('/api/FrontOffice/AppointmentBook/AttendAppointment', data).pipe(
      take(1),
      catchError((e) => {
        var result: APIResult = new APIResult();
        result.Success = 0;
        result.Msg = e;
        return EMPTY;
      }),
      map((m) => {
        return m;
      })
    );
  }

  UpdateAppointmentResource(AptId: number, ResourceId: number): Observable<Appointment> {
    var data = {
      AppointmentId: AptId,
      ResourceId: ResourceId
    };
    return this.http.post<any>('/api/FrontOffice/AppointmentBook/UpdateAppointmentResource', data).pipe(
      take(1),
      catchError((e) => {
        var result: APIResult = new APIResult();
        result.Success = 0;
        result.Msg = e;
        return EMPTY;
      }),
      map((m) => {
        return m;
      })
    );
  }

  MakeSaleAppointment(aptId: number): Observable<APIResult> {
    var data = {
      AppointmentId: aptId,
    };
    return this.http.post<APIResult>('/api/FrontOffice/AppointmentBook/SaleMade', data).pipe(
      take(1),
      catchError((e) => {
        var result: APIResult = new APIResult();
        result.Success = 0;
        result.Msg = e;
        return EMPTY;
      }),
      map((m) => {
        return m;
      })
    );
  }

  GetTimeLeftOnActiveAppointments(AppointmentId: number, BranchId: number, ClientId: number, SessionId: number): Observable<Resource[]> {
    const data = {
      AppointmentId: AppointmentId,
      BranchId: BranchId,
      ClientId: ClientId,
      SessionId: SessionId,
      RequestedStartTime: new Date(),
    };
    return this.http.post<Resource[]>('/api/FrontOffice/AppointmentBook/GetTimeLeftOnActiveAppointments', data).pipe(
      take(1),
      catchError((e) => {
        return EMPTY;
      }),
      map((model) => {
        return model.map((i) => Resource.CreateFrom(i));
      })
    );
  }


  GetResourcesAndWaitTimes(RequestedStartTime: Date, AppointmentId: number, BranchId: number,
    ClientId: number, SessionId: number, PreSessionDelay:number, PostSessionDelay:number): Observable<Resource[]> {
    const data = {
      AppointmentId: AppointmentId,
      BranchId: BranchId,
      ClientId: ClientId,
      SessionId: SessionId,
      RequestedStartTime: ConstantsService.MakeDateTimeGMT(RequestedStartTime),
      PreSessionDelay: PreSessionDelay,
      PostSessionDelay :PostSessionDelay,
    };
    return this.http.post<Resource[]>('/api/FrontOffice/AppointmentBook/GetResourceWaitTimes', data).pipe(
      take(1),
      catchError((e) => {
        return EMPTY;
      }),
      map((model) => {
        return model.map((i) => Resource.CreateFrom(i));
      })
    );
  }

  AppointmentStarted(req: StartAppointmentRequest): Observable<boolean> {
    var data = {
      AppointmentId: req.AppointmentId,
      ActualStartTime: dayjs(), // ConstantsService.MakeDateTimeGMT(new Date()),
      Duration: req.Duration,
      Delay: req.Delay,
      NetworkDeviceId: req.NetworkId,
      ResourceId: req.ResourceId,
    };
    return this.http.post<boolean>('/api/FrontOffice/AppointmentBook/AppointmentStarted', data).pipe(take(1));
  }

  AppointmentCompleted(req: StartAppointmentRequest): Observable<boolean> {
    var data = {
      AppointmentId: req.AppointmentId,
      ActualStartTime: dayjs(), // new Date(),
      Duration: req.Duration,
      Delay: req.Delay,
      NetworkDeviceId: req.NetworkId,
      ResourceId: req.ResourceId,
    };
    return this.http.post<boolean>('/api/FrontOffice/AppointmentBook/AppointmentCompleted', data).pipe(take(1));
  }


  GetResourceActiveApt(ResourceId: number): Observable<Appointment> {
    var data = {
      ResourceId: ResourceId,
    };
    return this.http.post<Appointment>('/api/FrontOffice/AppointmentBook/GetResourceActiveAppointment', data).pipe(
      take(1),
      catchError((e) => {
        return EMPTY;
      }),
      map((a) => {
        return Appointment.CreateAppointment(a);
      })
    );
  }

  removeAptFromQueue(aptId: number): Observable<APIResult> {
    return this.http
      .post<WebAPIResult>('/api/FrontOffice/AppointmentBook/RemoveAppointmentFromQueue', { AppointmentId: aptId })
      .pipe(
        take(1),
        catchError((e) => {
          var result: APIResult = new APIResult();
          result.Success = 0;
          result.Msg = e;
          return of(result);
        }),
        map((m) => {
          var result: APIResult = new APIResult();
          result.Success = m.Success;
          result.Msg = m.Msg;
          return result;
        })
      );
  }

  toggleAptLock(aptId: number, locked: boolean): Observable<APIResult> {
    return this.http
      .post<WebAPIResult>('/api/FrontOffice/AppointmentBook/ToggleAppointmentLock', {
        AppointmentId: aptId,
        Locked: locked,
      })
      .pipe(
        take(1),
        catchError((e) => {
          var result: APIResult = new APIResult();
          result.Success = 0;
          result.Msg = e;
          return of(result);
        }),
        map((m) => {
          var result: APIResult = new APIResult();
          result.Success = m.Success;
          result.Msg = m.Msg;
          return result;
        })
      );
  }

  updateAppointmentDelay(aptInfo: SessionDelay): Observable<APIResult> {
    return this.http
      .post<WebAPIResult>('/api/FrontOffice/AppointmentBook/UpdateAppointmentDelays', {
        AppointmentId: aptInfo.AptId,
        PreSessionDelay: aptInfo.preSessionDelay,
        PostSessionDelay: aptInfo.postSessionDelay,
      })
      .pipe(
        take(1),
        catchError((e) => {
          var result: APIResult = new APIResult();
          result.Success = 0;
          result.Msg = e;
          return of(result);
        }),
        map((m) => {
          var result: APIResult = new APIResult();
          result.Success = m.Success;
          result.Msg = m.Msg;
          return result;
        })
      );
  }

  getConflictingAppointment(bookAptInfo: BookAptInfo): Observable<Appointment> {
    return this.http
      .post<Appointment>('/api/FrontOffice/AppointmentBook/GetConflictingAppointment', {
        AppointmentId: bookAptInfo.appointmentId,
        BranchId: bookAptInfo.branchId,
        ResourceId: bookAptInfo.resourceId,
        StartDateTime: bookAptInfo.startTime,
        EndDateTime: bookAptInfo.endTime,
        PreSessionDelay: bookAptInfo.PreSessionDelay,
        PostSessionDelay: bookAptInfo.PostSessionDelay,
      })
      .pipe(take(1));
  }

  getConflictingAppointmentExists(newAppointment: Appointment, existingAppointments: Appointment[]): boolean {
    const newStartDateTime = ConstantsService.MakeDateTimeGMT(newAppointment.StartDateTime);
    const newEndDateTime = ConstantsService.MakeDateTimeGMT(newAppointment.EndDateTime);

    const length = existingAppointments.length;
    for (let i = 0; i < length; i++) {
      let exisitingAppointment: Appointment = existingAppointments[i];
      const exisitingAppointmentStartDateTime = ConstantsService.MakeDateTimeGMT(exisitingAppointment.StartDateTime);
      const exisitingAppointmentEndDateTime = ConstantsService.MakeDateTimeGMT(exisitingAppointment.EndDateTime);

      if (exisitingAppointment.State != AppointmentState.Completed) {
        if (
          (newStartDateTime >= exisitingAppointmentStartDateTime &&
            newStartDateTime < exisitingAppointmentEndDateTime) ||
          (newEndDateTime > exisitingAppointmentStartDateTime && newEndDateTime <= exisitingAppointmentEndDateTime) ||
          (newStartDateTime <= exisitingAppointmentStartDateTime && newEndDateTime >= exisitingAppointmentEndDateTime)
        ) {
          return true;
        }
      }
    }

    return false;
  }
}
